/**
 * AdminUsers Page
 * 사용자 목록 및 관리
 */
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { UserListItem } from '../../components/molecules/UserListItem';
import { UserGradeModal } from '../../components/organisms/UserGradeModal';
import { adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

export const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // 페이징 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pageHistory, setPageHistory] = useState<Map<number, string | null>>(new Map([[1, null]])); // 각 페이지의 시작 UID
  const isInitialMount = useRef(true); // 초기 마운트 감지

  // 등급별 사용자 수
  const [usersByGrade, setUsersByGrade] = useState<Record<string, number>>({
    admin: 0,
    member: 0,
    regular: 0,
    special: 0
  });

  useEffect(() => {
    // 초기 로드 시 등급별 사용자 수 조회 및 사용자 목록 로드
    loadUsersByGrade();
    loadUsers();
  }, []);

  useEffect(() => {
    // 필터가 변경되면 첫 페이지로 리셋
    if (currentPage === 1) {
      // 이미 1페이지면 직접 로드
      setPageHistory(new Map([[1, null]])); // 히스토리 초기화
      loadUsers();
    } else {
      // 1페이지가 아니면 페이지를 1로 설정 (currentPage useEffect에서 로드)
      setCurrentPage(1);
      setPageHistory(new Map([[1, null]])); // 히스토리 초기화
    }
  }, [filter]);

  useEffect(() => {
    // 검색어가 변경되면 첫 페이지로 리셋하고 로드
    if (currentPage === 1) {
      setPageHistory(new Map([[1, null]]));
      loadUsers();
    } else {
      setCurrentPage(1);
      setPageHistory(new Map([[1, null]]));
    }
  }, [searchQuery]);

  useEffect(() => {
    // 페이지가 변경되면 로드 (초기 마운트 시 제외)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadUsers();
  }, [currentPage]);

  const loadUsersByGrade = async () => {
    try {
      const response: any = await adminAPI.getAnalytics();
      if (response.success) {
        setUsersByGrade(response.data.usersByGrade);
      }
    } catch (err: any) {
      console.error('Failed to load users by grade:', err);
      // 에러가 발생해도 사용자 목록은 계속 로드
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize
      };

      if (filter) {
        params.memberGrade = filter;
      }

      // 검색어 추가
      if (searchQuery && searchQuery.trim()) {
        params.searchQuery = searchQuery.trim();
      }

      // 1페이지가 아닐 때만 startAfter 사용
      if (currentPage > 1) {
        const startAfterUid = pageHistory.get(currentPage);
        if (startAfterUid) {
          params.startAfter = startAfterUid;
        }
      }

      const response: any = await adminAPI.getUsersList(params);

      if (response.success) {
        // Date 문자열을 Date 객체로 변환
        const usersWithDates = response.data.users.map((user: any) => {
          // createdAt 처리: ISO 문자열을 Date 객체로 변환, 실패 시 null
          let createdAt = null;
          if (user.createdAt) {
            const parsed = new Date(user.createdAt);
            if (!isNaN(parsed.getTime())) {
              createdAt = parsed;
            } else {
              console.warn('[AdminUsers] Invalid createdAt for user:', user.uid, user.createdAt);
            }
          }

          // lastLoginAt 처리: ISO 문자열을 Date 객체로 변환, 실패 시 null
          let lastLoginAt = null;
          if (user.lastLoginAt) {
            const parsed = new Date(user.lastLoginAt);
            if (!isNaN(parsed.getTime())) {
              lastLoginAt = parsed;
            } else {
              console.warn('[AdminUsers] Invalid lastLoginAt for user:', user.uid, user.lastLoginAt);
            }
          }

          return {
            ...user,
            createdAt,
            lastLoginAt
          };
        });

        setUsers(usersWithDates);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);

        // 다음 페이지의 startAfter UID 저장 (현재 페이지의 마지막 UID)
        if (usersWithDates.length > 0) {
          const lastUserUid = usersWithDates[usersWithDates.length - 1].uid;

          // 다음 페이지가 존재하면 히스토리에 저장
          if (currentPage < response.data.totalPages) {
            setPageHistory(prev => {
              const newHistory = new Map(prev);
              newHistory.set(currentPage + 1, lastUserUid);
              return newHistory;
            });
          }
        }
      }
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError('사용자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 백엔드에서 이미 필터링된 사용자 목록 사용

  const handleGradeChange = (uid: string) => {
    const user = users.find(u => u.uid === uid);
    if (user) {
      setSelectedUser(user);
      setModalOpen(true);
    }
  };

  const handleGradeUpdate = async (uid: string, newGrade: string) => {
    await adminAPI.updateUserGrade(uid, newGrade);
    // 목록 새로고침
    await loadUsers();
  };

  const handleGoToFirstPage = () => {
    setCurrentPage(1);
    // useEffect가 데이터를 로드함
  };

  return (
    <>
      <Header>
          <HeaderContent>
            <BackButton onClick={() => navigate('/admin')}>
              ← 대시보드로
            </BackButton>
            <Title>사용자 관리</Title>
            <Subtitle>{totalCount}명의 사용자</Subtitle>
          </HeaderContent>

          <SearchBar>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>

          <Filters>
            <FilterButton
              $isActive={filter === ''}
              onClick={() => setFilter('')}
            >
              전체 ({(usersByGrade.member + usersByGrade.regular + usersByGrade.special + usersByGrade.admin).toLocaleString()}명)
            </FilterButton>
            <FilterButton
              $isActive={filter === 'member'}
              onClick={() => setFilter('member')}
            >
              일반회원 ({usersByGrade.member.toLocaleString()}명)
            </FilterButton>
            <FilterButton
              $isActive={filter === 'regular'}
              onClick={() => setFilter('regular')}
            >
              정회원 ({usersByGrade.regular.toLocaleString()}명)
            </FilterButton>
            <FilterButton
              $isActive={filter === 'special'}
              onClick={() => setFilter('special')}
            >
              특별회원 ({usersByGrade.special.toLocaleString()}명)
            </FilterButton>
          </Filters>
        </Header>

        <Content>
          {loading && (
            <LoadingMessage>사용자 목록을 불러오는 중...</LoadingMessage>
          )}

          {error && (
            <ErrorMessage>{error}</ErrorMessage>
          )}

          {!loading && !error && users.length === 0 && (
            <EmptyMessage>
              <EmptyIcon>👤</EmptyIcon>
              <EmptyText>
                {searchQuery ? '검색 결과가 없습니다' : '사용자가 없습니다'}
              </EmptyText>
            </EmptyMessage>
          )}

          {!loading && !error && users.length > 0 && (
            <>
              <UsersList>
                {users.map(user => (
                  <UserListItem
                    key={user.uid}
                    user={user}
                    onGradeChange={handleGradeChange}
                  />
                ))}
              </UsersList>

              {/* 페이징 UI */}
              <Pagination>
                <PaginationInfo>
                  전체 {totalCount}명 중 {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)}명 표시
                </PaginationInfo>

                <PaginationControls>
                  <PageButton
                    onClick={handleGoToFirstPage}
                    disabled={currentPage === 1}
                  >
                    처음
                  </PageButton>
                  <PageButton
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    이전
                  </PageButton>

                  <PageInfo>
                    {currentPage} / {totalPages} 페이지
                  </PageInfo>

                  <PageButton
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    다음
                  </PageButton>
                </PaginationControls>

                <PageSizeSelector>
                  <label>페이지당 표시: </label>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                      setPageHistory(new Map([[1, null]])); // 히스토리 초기화
                    }}
                  >
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                    <option value={100}>100개</option>
                  </select>
                </PageSizeSelector>
              </Pagination>
            </>
          )}
        </Content>

      <UserGradeModal
        isOpen={modalOpen}
        user={selectedUser}
        onClose={() => {
          setModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleGradeUpdate}
      />
    </>
  );
};

const Header = styled.div`
  padding: ${tokens.spacing[6]};
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
`;

const HeaderContent = styled.div`
  margin-bottom: ${tokens.spacing[4]};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.primary[500]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  padding: ${tokens.spacing[2]} 0;
  margin-bottom: ${tokens.spacing[3]};
  transition: color ${tokens.animation.duration.fast};

  &:hover {
    color: ${tokens.colors.primary[600]};
  }
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin: 0 0 ${tokens.spacing[2]} 0;
`;

const Subtitle = styled.p`
  font-size: ${tokens.typography.fontSize.base};
  color: ${tokens.colors.neutral[600]};
  margin: 0;
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${tokens.spacing[4]};
  max-width: 500px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${tokens.spacing[4]};
  top: 50%;
  transform: translateY(-50%);
  font-size: ${tokens.typography.fontSize.lg};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]} ${tokens.spacing[3]} ${tokens.spacing[12]};
  border: 2px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.base};
  font-family: ${tokens.typography.fontFamily.primary};
  color: ${tokens.colors.neutral[800]};
  transition: all ${tokens.animation.duration.fast};

  &::placeholder {
    color: ${tokens.colors.neutral[500]};
  }

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary[500]};
    box-shadow: 0 0 0 3px ${tokens.colors.primary[500]}20;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $isActive: boolean }>`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  border: 2px solid ${props => props.$isActive ? tokens.colors.primary[500] : tokens.colors.neutral[300]};
  background: ${props => props.$isActive ? tokens.colors.primary[500] : tokens.colors.neutral[0]};
  color: ${props => props.$isActive ? tokens.colors.neutral[0] : tokens.colors.neutral[700]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.fast};

  &:hover {
    border-color: ${tokens.colors.primary[500]};
  }
`;

const Content = styled.div`
  padding: ${tokens.spacing[6]};
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[4]};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  font-size: ${tokens.typography.fontSize.lg};
  color: ${tokens.colors.neutral[500]};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[6]};
  background: ${tokens.colors.semantic.error}15;
  color: ${tokens.colors.semantic.error};
  border-radius: ${tokens.borderRadius.md};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${tokens.spacing[4]};
`;

const EmptyIcon = styled.div`
  font-size: ${tokens.typography.fontSize['6xl']};
`;

const EmptyText = styled.p`
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[700]};
  margin: 0;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${tokens.spacing[6]};
  padding-top: ${tokens.spacing[6]};
  border-top: 1px solid ${tokens.colors.neutral[200]};
  gap: ${tokens.spacing[4]};

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PaginationInfo = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
  align-items: center;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  border: 1px solid ${tokens.colors.neutral[300]};
  background: ${props => props.disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[0]};
  color: ${props => props.disabled ? tokens.colors.neutral[400] : tokens.colors.neutral[700]};
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all ${tokens.animation.duration.fast};

  &:hover:not(:disabled) {
    background: ${tokens.colors.neutral[50]};
    border-color: ${tokens.colors.primary[500]};
  }

  &:active:not(:disabled) {
    background: ${tokens.colors.neutral[100]};
  }
`;

const PageInfo = styled.div`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  white-space: nowrap;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing[2]};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};

  label {
    white-space: nowrap;
  }

  select {
    padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
    border: 1px solid ${tokens.colors.neutral[300]};
    border-radius: ${tokens.borderRadius.md};
    font-size: ${tokens.typography.fontSize.sm};
    font-family: ${tokens.typography.fontFamily.primary};
    color: ${tokens.colors.neutral[800]};
    cursor: pointer;
    transition: border-color ${tokens.animation.duration.fast};

    &:focus {
      outline: none;
      border-color: ${tokens.colors.primary[500]};
    }

    &:hover {
      border-color: ${tokens.colors.primary[500]};
    }
  }
`;
